import { Injectable, Optional } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

import { StorageKeys, StorageService } from "./storage.service";

export interface BetaFilters {
  type: string;
  status: string;
  reviewStatus: string;
  author: string;
  jiraTicketNumber: string;
  sort: string;
}

@Injectable({
  providedIn: "root",
})
export class BetaCommunityService {
  public betaFilterReadableValues: any = {
    bug: "Bug",
    feature_request: "Feature Request",
    post: "Post",
    none: "None",
    open: "Open",
    cannot_reproduct: "Cannot Reproduce",
    fixed: "Fixed",
    investigating: "Investigating",
    accepted: "Accepted",
    rejected: "Rejected",
    duplicate: "Duplicate",
    by_design: "By Design",
    true: "Only Reviewed",
    false: "Only Not Reviewed",
  };

  public filters: BetaFilters = {
    type: "all",
    status: "all",
    reviewStatus: "all",
    author: "all",
    jiraTicketNumber: "",
    sort: "date",
  };

  public showBetaType: string;
  public showBetaStatus: string;
  public showBetaReviewStatus: any;
  public showBetaAuthor: any;
  public betaSortBy: string;
  public findJiraTicketNumber: any;

  private _betaFilterSettings: any;

  constructor(
    private storage: StorageService,
    private translate: TranslateService
  ) {}

  buildFilters(queryParams: Record<string, any> = {}) {
    this.filters = {
      type: this.showBetaType || queryParams["type"] || "all",
      status: this.showBetaStatus || queryParams["status"] || "all",
      reviewStatus: this.showBetaReviewStatus || queryParams["reviewStatus"] || "all",
      author: this.showBetaAuthor || queryParams["author"] || "all",
      sort: this.betaSortBy || queryParams["sort"] || "date",
      jiraTicketNumber: this.findJiraTicketNumber || "",
    };
    this.applyFilters(this.filters, false);
    return this.filters;
  }

  applyFilters(filters: BetaFilters, resetJira = true) {
    const nextFilters = { ...filters };
    if (resetJira && nextFilters.jiraTicketNumber) {
      nextFilters.type = "all";
      nextFilters.status = "all";
      nextFilters.reviewStatus = "all";
      nextFilters.author = "all";
      nextFilters.sort = "date";
    }

    this.filters = nextFilters;
    this.showBetaType = nextFilters.type === "all" ? undefined : nextFilters.type;
    this.showBetaStatus = nextFilters.status === "all" ? undefined : nextFilters.status;
    this.showBetaReviewStatus = nextFilters.reviewStatus === "all" ? undefined : nextFilters.reviewStatus;
    this.showBetaAuthor = nextFilters.author === "all" ? undefined : nextFilters.author;
    this.betaSortBy = nextFilters.sort === "date" ? undefined : nextFilters.sort;
    this.findJiraTicketNumber = nextFilters.jiraTicketNumber || undefined;

    return this.filters;
  }

  get hasActiveFilters(): boolean {
    return !!(this.showBetaType || this.showBetaStatus || this.showBetaReviewStatus != null || this.showBetaAuthor || this.findJiraTicketNumber);
  }

  buildAlgoliaFilters(betaStringId: string, channel?: any, userId?: string, opts?: { excludeAuthor?: boolean }): string {
    const parts: string[] = [`beta_string_id:"${betaStringId}"`];

    // Channel filter (if specific channel selected)
    if (channel && channel.string_id !== "all_posts" && channel.string_id !== "pinned_posts" && channel.string_id !== "my_posts") {
      parts.push(`channels:"${channel.string_id}"`);
    }

    // "My Posts" channel
    if (channel && channel.string_id === "my_posts" && userId) {
      parts.push(`author.id:"${userId}"`);
    }

    if (this.findJiraTicketNumber) {
      parts.push(`beta_review_details.jira_ticket_number:"${this.findJiraTicketNumber}"`);
      return parts.join(" AND ");
    }

    if (this.showBetaType === "none") {
      // Algolia has no "is missing" filter, so match "none" by excluding every known type.
      // String-attribute NOT filters include records where the attribute is absent entirely,
      // which is exactly what we want for un-typed posts.
      parts.push(`NOT beta_type:"bug"`, `NOT beta_type:"feature_request"`, `NOT beta_type:"post"`);
    } else if (this.showBetaType) {
      parts.push(`beta_type:"${this.showBetaType}"`);
    }
    if (this.showBetaStatus === "none") {
      parts.push(
        `NOT beta_status:"open"`,
        `NOT beta_status:"cannot_reproduce"`,
        `NOT beta_status:"fixed"`,
        `NOT beta_status:"investigating"`,
        `NOT beta_status:"accepted"`,
        `NOT beta_status:"rejected"`,
        `NOT beta_status:"duplicate"`,
        `NOT beta_status:"by_design"`
      );
    } else if (this.showBetaStatus) {
      parts.push(`beta_status:"${this.showBetaStatus}"`);
    }
    if (this.showBetaReviewStatus != null) {
      parts.push(`reviewed:${this.showBetaReviewStatus}`);
    }
    if (this.showBetaAuthor && !opts?.excludeAuthor) parts.push(`author.id:"${this.showBetaAuthor}"`);

    return parts.join(" AND ");
  }

  resetFilters() {
    const defaults: BetaFilters = {
      type: "all",
      status: "all",
      reviewStatus: "all",
      author: "all",
      sort: "date",
      jiraTicketNumber: "",
    };
    return this.applyFilters(defaults, false);
  }

  getQueryParams(searchQuery?: string) {
    const queryParams: any = {
      type: this.showBetaType,
      status: this.showBetaStatus,
      reviewStatus: this.showBetaReviewStatus,
      author: this.showBetaAuthor,
      sort: this.betaSortBy,
    };

    if (searchQuery) {
      queryParams.search = searchQuery;
    }

    Object.keys(queryParams).forEach((key) => {
      if (queryParams[key] === undefined) {
        delete queryParams[key];
      }
    });

    return queryParams;
  }

  get betaFilterSettings() {
    if (!this._betaFilterSettings) {
      this._betaFilterSettings = this.storage.getItem(StorageKeys.BETA_FILTER_SETTINGS) || {};
    }
    return this._betaFilterSettings;
  }

  set betaFilterSettings(value: any) {
    if (value) {
      this._betaFilterSettings = value;
      this.storage.setItem(StorageKeys.BETA_FILTER_SETTINGS, value);
    } else {
      this._betaFilterSettings = undefined;
      this.storage.removeItem(StorageKeys.BETA_FILTER_SETTINGS);
    }
  }

  getViewMode(defaultValue = "list") {
    return this.betaFilterSettings.viewMode || defaultValue;
  }

  setViewMode(view: string) {
    this.betaFilterSettings = {
      ...this.betaFilterSettings,
      viewMode: view,
    };
  }

  getAnnouncementsCollapsed(defaultValue = false) {
    return this.betaFilterSettings.announcementsCollapsed !== undefined ? this.betaFilterSettings.announcementsCollapsed : defaultValue;
  }

  setAnnouncementsCollapsed(collapsed: boolean) {
    this.betaFilterSettings = {
      ...this.betaFilterSettings,
      announcementsCollapsed: collapsed,
    };
  }

  fetchBetaAnnouncements(betaStringId: string, collectionRef?: any) {
    const collection = collectionRef;
    if (!collection || !betaStringId) return Promise.resolve([]);
    return collection.ref
      .where("parentID", "==", null)
      .where("beta_string_id", "==", betaStringId)
      .where("pinned", "==", true)
      .get()
      .then((result) => {
        let pinnedPosts = [];
        result.forEach((doc) => {
          let post: any = doc.data();
          if (!post.deleted) {
            post.id = doc.id;
            // Every other loader sets both; announcements need `key` too because the template binds
            // replies off activeParentThreadMap[ann.key] and updatePost() writes to `${collection}/${key}`.
            post.key = doc.id;
            pinnedPosts.push(post);
          }
        });
        return pinnedPosts;
      });
  }

  /**
   * Checks if a post matches the current beta filters
   * @param post The post to check
   * @returns true if the post matches all active filters, false otherwise
   */
  matchesFilters(post: any): boolean {
    // Helper function to validate filter values
    const validateFilterValue = (value: any): string | null => {
      if (value === undefined || value === null || value === "") return null;
      const stringValue = typeof value === "string" && value.trim() ? value.trim() : null;
      return stringValue && stringValue !== "all" ? stringValue : null;
    };

    // Check Jira ticket number first (if set, it's the only filter)
    const jiraTicketNumber = validateFilterValue(this.findJiraTicketNumber);
    if (jiraTicketNumber) {
      const matches = post.beta_review_details?.jira_ticket_number === jiraTicketNumber;
      return matches;
    }

    // Check other filters
    const betaType = validateFilterValue(this.showBetaType);
    if (betaType && post.beta_type !== betaType) {
      return false;
    }

    const betaStatus = validateFilterValue(this.showBetaStatus);
    if (betaStatus === "none") {
      if (post.beta_status && post.beta_status !== "none") return false;
    } else if (betaStatus && post.beta_status !== betaStatus) {
      return false;
    }

    const betaReviewStatus = validateFilterValue(this.showBetaReviewStatus);
    if (betaReviewStatus !== null) {
      const isReviewed = betaReviewStatus === "true";
      if (post.reviewed !== isReviewed) {
        return false;
      }
    }

    const betaAuthor = validateFilterValue(this.showBetaAuthor);
    if (betaAuthor && post.author?.id !== betaAuthor) {
      return false;
    }

    return true;
  }

  /**
   * Checks if a post should be shown for a specific channel
   * @param post The post to check
   * @param channelId The channel ID (e.g., "my_posts", "all_posts")
   * @param userId The current user's ID (required for "my_posts" filtering)
   * @returns true if the post should be shown for this channel, false otherwise
   */
  shouldShowPostForChannel(post: any, channelId: string, userId?: string): boolean {
    // For "my_posts" channel, filter by author
    if (channelId === "my_posts") {
      if (!userId) {
        return false;
      }
      const matches = post.author?.id === userId;
      return matches;
    }

    // For other channels, always return true (channel filtering is done in Firestore query)
    return true;
  }

  /**
   * Checks if a post matches both channel requirements and beta filters
   * @param post The post to check
   * @param channelId The channel ID (e.g., "my_posts", "all_posts")
   * @param userId The current user's ID (required for "my_posts" filtering)
   * @returns true if the post should be displayed, false otherwise
   */
  shouldDisplayPost(post: any, channelId: string, userId?: string): boolean {
    // First check channel-specific requirements (like "my_posts")
    if (!this.shouldShowPostForChannel(post, channelId, userId)) {
      return false;
    }

    // Then check beta filters
    if (!this.matchesFilters(post)) {
      return false;
    }

    return true;
  }

  /**
   * Filters an array of posts based on channel and beta filters
   * @param posts Array of posts to filter
   * @param channelId The channel ID (e.g., "my_posts", "all_posts")
   * @param userId The current user's ID (required for "my_posts" filtering)
   * @returns Filtered array of posts
   */
  filterPosts(posts: any[], channelId: string, userId?: string): any[] {
    const filtered = posts.filter((post) => this.shouldDisplayPost(post, channelId, userId));
    return filtered;
  }
}
